import { filterMask } from '@sda/sdk/helpers/filter-mask';
import { SDKOptions } from '@sda/sdk/helpers/sdk.options';
import { QueryHelper } from '@sda/sdk/helpers/query.helpers';
import type { Reducers } from '@sda/enums/store';
import type { GenericList } from '@sda/helpers/helpers.types';
import type {
  FetchDataType, FirestoreClientData, QueryFilterMask, QueryOptions,
  SetDataType, SnapshotQueryCallback, SnapshotQueryOptions, SnapshotQueryResult,
  SnapshotHasId, SnapshotListenerOptions, QueryObjectCallback,
} from '../interfaces';
import type { Batch } from '../lib/batch';
import type { Transaction } from './firestore.helpers/transaction';
import type { BaseHelpers } from './firestore.helpers/base.helpers';
import type FirebaseFirestore from 'firebase/firestore';

/**
 * This class is an intermediary between the SDK Base Class and the actual, per-data-type implementations.
 * It takes care of implementing the methods outlined in the SDKBaseClass specifically with regard to Firestore.
 */
export class SDK<T> {
  public fsh: BaseHelpers;

  public collection: FirebaseFirestore.CollectionReference<T>;

  public listenedIds: { [snapshotId: string]: SnapshotQueryResult };

  public documentId: string;

  public PublicMap: QueryFilterMask;

  public PrivateMap: QueryFilterMask;

  constructor(apiClient: FirestoreClientData, reducerName?: Reducers | ((item: T) => Reducers)) {
    this.fsh = new SDKOptions.BaseFirestoreHelpers(apiClient, reducerName);

    this.collection = null;
    this.listenedIds = {};
  }

  /**
   * Returns whether or not the object exists in the collection
   * @param  {string} id
   * @return {Promise<boolean>}
   */
  async exists(id: string): Promise<boolean> {
    return this.fsh.hasRecord(this.collection, this.documentId || id);
  }

  /**
   * Create a new object in the collection.
   * Returns the ID of the created record
   * @param  {Partial<T>} data
   * @param  {string} [id]
   * @return {Promise<string>}
   */
  async create(data: Partial<T & SetDataType>, id?: string): Promise<string> {
    const newDoc = await this.fsh.addRecord<T>(
      this.collection,
      { propagate: true, ...data } as T & SetDataType,
      this.documentId || id,
    );

    return newDoc.id;
  }

  /**
   * Create a new object in the collection.
   * Alias for create
   * @param  {Partial<T>} data
   * @param  {string} [id]
   * @return {Promise<string>}
   */
  async add(data: Partial<T & SetDataType>, id?: string): Promise<string> {
    return this.create(data, id);
  }

  /**
   * Retrieves the requested document reference
   * @param  {string} id
   * @return {FirebaseFirestore.DocumentReference}
   */
  getDocumentReference(id: string): FirebaseFirestore.DocumentReference {
    return this.fsh.getDocumentReference(this.collection, this.documentId || id);
  }

  /**
   * Returns a count of the number of rows in a collection
   * @param  {QueryOptions} options
   * @return {Promise<number>}
   */
  async count(options: QueryOptions<T> = {}): Promise<number> {
    const count = await this.fsh.countRecords<T>(this.collection, options);

    return count;
  }

  /**
   * Create a new object if none exists, otherwise update it
   * @param  {any} data
   * @param  {string} [id]
   * @return {Promise<FirebaseFirestore.DocumentReference>}
   */
  async upsert(data: Partial<T>, id?: string): Promise<FirebaseFirestore.DocumentReference | string> {
    if (!id && !this.documentId) {
      return this.create(data, this.documentId || id);
    }

    return this.fsh.updateCreateRecord<T>(this.collection, this.documentId || id, { ...data, propagate: false } as T & SetDataType);
  }

  /**
   * List object in the collection.
   * @param  {string} [orderBy]
   * @return {Promise<any>}
   */
  async list(options: QueryOptions = {}): Promise<GenericList<T & FetchDataType>> {
    return this.fsh.listRecords<T>(this.collection, options);
  }

  /**
   * Get one or more objects by a specific document attribute
   * @param  {any|any[]} query
   * @return {Promise<any>}
   */
  async listBy(callback: QueryObjectCallback<T>, options?: QueryOptions<T>): Promise<GenericList<T & FetchDataType>> {
    const query = callback(new QueryHelper<T>());
    return this.fsh.listRecordsBy<T>(this.collection, query, options);
  }

  /**
   * Get a list of objects by an array of IDs
   * @param  {string[]} ids
   * @return {Promise<any>}
   */
  async listIn(ids: string[]): Promise<GenericList<T & FetchDataType>> {
    const records = await this.fsh.listRecordsIn<T>(this.collection, ids);

    return records;
  }

  /**
   * Get an object from the collection.
   * @param  {string} id
   */
  async get(id?: string): Promise<T & FetchDataType> {
    if (!this.documentId && !id) {
      console.log('No id passed to get');

      return null;
    }

    return this.fsh.getRecord<T>(this.collection, this.documentId || id);
  }

  protected async getCombinedRecord<P>(id: string, collection: FirebaseFirestore.CollectionReference): Promise<T & P & FetchDataType> {
    const record = await this.fsh.getRecord<T>(this.collection, this.documentId || id);
    const secondaryRecord = await this.fsh.getRecord<P>(collection, this.documentId || id);

    if (!record && !secondaryRecord) {
      return null;
    }

    return {
      ...record || {},
      ...secondaryRecord || {},
    } as T & P & FetchDataType;
  }

  /**
   * Get the first object of a search by a specific document attribute
   * @param  {any|any[]} query
   * @return {Promise<any>}
   */
  async getBy(queryCallback: QueryObjectCallback<T>, options?: QueryOptions<T>): Promise<T & FetchDataType> {
    const query = queryCallback(new QueryHelper<T>());
    const records = await this.fsh.listRecordsBy<T>(this.collection, query, { ...options || {}, limit: 1 });

    if (records) {
      const [key] = Object.keys(records);

      return records[key];
    }

    return null;
  }

  /**
   * Update an object in the collection.
   * @param  {string} id
   * @param  {any} data
   * @return {Promise<FirebaseFirestore.DocumentReference>}
   */
  update = async (
    id: string,
    data: Partial<T & SetDataType>,
  ): Promise<FirebaseFirestore.DocumentReference | FirebaseFirestore.Transaction> => this.fsh.updateRecord<T>(
    this.collection,
    this.documentId || id,
    {
      ...data,
      propagate: false,
    },
  );

  /**
   * Update an object in the collection and propagate the update to its triggers
   * @param  {string} id
   * @param  {any} data
   * @return {Promise<FirebaseFirestore.DocumentReference>}
   */
  propagate = async (
    id: string,
    data: Partial<T & SetDataType>,
  ): Promise<FirebaseFirestore.DocumentReference | FirebaseFirestore.Transaction> => this.fsh.updateRecord<T>(
    this.collection,
    this.documentId || id,
    {
      ...data,
      propagate: true,
    },
  );

  /**
   * Update many objects by id
   * @deprecated
   */
  async updateBatch(data: GenericList<Partial<T & SetDataType>>): Promise<void | T> {
    if (!data || Object.keys(data).length === 0) {
      return null;
    }

    return this.fsh.updateBatch<T>(this.collection, data);
  }

  /**
   * Set an object in the collection (replaces all existing data).
   */
  set = async (
    id: string,
    data: Partial<T & SetDataType>,
  ): Promise<FirebaseFirestore.DocumentReference | FirebaseFirestore.Transaction> => this.fsh.setRecord<T>(this.collection, this.documentId || id, {
    propagate: true,
    ...data,
  });

  /**
   * Create many objects by id
   * @deprecated
   */
  async setBatch(data: GenericList<Partial<T & SetDataType>>): Promise<void | T> {
    if (!data || Object.keys(data).length === 0) {
      return null;
    }

    return this.fsh.setBatch<T>(this.collection, data);
  }

  /**
   * Add many objects
   * @deprecated
   */
  async addBatch(data: GenericList<Partial<T & SetDataType>>): Promise<void | T> {
    if (!data || Object.keys(data).length === 0) {
      return null;
    }

    return this.fsh.addBatch<T>(this.collection, data);
  }

  /**
   * Gets a batch object that can be used to add/set/update items one at a time
   * @return {Batch}
   */
  getBatch(): Batch<T> {
    return this.fsh.getBatch<T>(this.collection);
  }

  /**
   * Pass a transaction to the Firestore helper that can be used to run CRUD options
   * @param  {FirebaseFirestore.Transaction}
   * @return {any}
   */
  transaction(t: FirebaseFirestore.Transaction): Transaction {
    return this.fsh.getTransaction(this.collection, t);
  }

  /**
   * Deletes an object in the collection.
   * Alias for `delete`
   * @param  {string} id
   * @return {Promise<any>}
   */
  async remove(id: string): Promise<void | FirebaseFirestore.Transaction> {
    return this.delete(this.documentId || id);
  }

  /**
   * Deletes an object in the collection.
   * @param  {string} id
   * @return {Promise<any>}
   */
  async delete(id: string): Promise<void | FirebaseFirestore.Transaction> {
    return this.fsh.deleteRecord(this.collection, this.documentId || id);
  }

  /**
   * Generate a unique identifier for the next record in a collection
   * @return {string}
   */
  getNextId(): string {
    return this.fsh.getNextId(this.collection);
  }

  /**
   * Subscribe to changes to a collection or object by ID
   * @param  {string} id {optional}
   * @param  {string[]} changeTypes {optional}
   * @param  {object[]} queries {optional}
   * @param  {number} limit
   * @param  {function} callback
   * @return {Function}
   */
  watch<S = SnapshotQueryOptions<T>>(options: SnapshotQueryOptions<T>, callback?: SnapshotQueryCallback<T, S>): SnapshotQueryResult {
    return this.fsh.watchRecord<T, S>(this.collection, options, callback);
  }

  listen<S = SnapshotHasId>(
    id: string,
    callback?: SnapshotQueryCallback<T, S>,
    options: SnapshotListenerOptions = { forceNewListener: false, modifier: null },
  ): SnapshotQueryResult {
    const { forceNewListener = false } = options;

    const docId = this.documentId || id;

    if (forceNewListener === true || !this.listenedIds[docId]) {
      if (forceNewListener === true && this.listenedIds[docId]) {
        this.listenedIds[docId]();
      }

      this.listenedIds[docId] = this.fsh.watchRecord<T, S>(this.collection, { id: docId, modifier: options?.modifier }, callback);

      return this.listenedIds[docId];
    }

    console.warn(`Already listening to ${docId}`);

    return null;
  }

  unsubscribe(id: string): void {
    const docId = this.documentId || id;

    if (this.listenedIds[docId]) {
      this.listenedIds[docId]();

      delete this.listenedIds[docId];
    }
  }

  /**
   * Forces a set of data to conform to the provided mapping helper
   *
   * @param  {T} data
   * @param  {any} mapHelper
   * @param  {boolean} patchOnly
   * @return {T}
   */
  filter = (
    data: Partial<T>,
    mapHelper: QueryFilterMask,
    patchOnly = false,
  ): T => filterMask<T>(data, mapHelper, patchOnly);
}
